/**
 * CONNECTION ADD-ONS (PLUGINS.md "addOns") — extra setups a plugin's connection offers, shown once the connection
 * itself works: download the add-on's own template (deploy it in the outside system), then Check — the plugin asks the
 * outside system, through this connection's identity, whether it is set up and enforces what it promises. Generic:
 * every word comes from the plugin.
 */
import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Download, Loader2, ShieldCheck, XCircle } from 'lucide-react';
import { getConnectionTemplate, testSecretsRegistrySecret } from '../../services/toolsApi';

const btn = 'inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md transition-colors';

export default function ConnectionAddOns({ spec, secretId, included = [], autoCheck = [] }) {
  // an add-on the connection's own template carries but the person turned off isn't shown; the ones it carries need
  // no separate template (it's already in the stack) and are CHECKED right away (ONE-STEP setup)
  const addOns = (spec?.addOns || []).filter((a) => !a.inTemplate || included.includes(a.id));
  const [busy, setBusy] = useState('');
  const [res, setRes] = useState({});              // add-on id → the last check
  const [err, setErr] = useState('');
  const ran = useRef('');
  useEffect(() => {
    const ids = addOns.filter((a) => a.check && autoCheck.includes(a.id)).map((a) => a.id);
    const key = `${secretId}:${ids.join(',')}`;
    if (!secretId || !ids.length || ran.current === key) return;
    ran.current = key;
    (async () => {
      for (const id of ids) {
        setBusy(`c:${id}`);
        try { const r = await testSecretsRegistrySecret(secretId, { addOn: id }); setRes((p) => ({ ...p, [id]: r })); }
        catch (e) { setErr(e?.message || "Couldn't run the check"); }
      }
      setBusy('');
    })();
  }, [secretId, autoCheck.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!addOns.length || !secretId) return null;
  const download = async (a) => {
    setBusy(`t:${a.id}`); setErr('');
    try {
      const t = await getConnectionTemplate(spec.id, { addOn: a.id });
      const url = URL.createObjectURL(new Blob([t?.template || ''], { type: 'text/yaml' }));
      const el = document.createElement('a');
      el.href = url; el.download = t?.fileName || `${a.id}.yaml`; el.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { setErr(e?.message || "Couldn't load the template"); }
    setBusy('');
  };
  const check = async (a) => {
    setBusy(`c:${a.id}`); setErr('');
    try { const r = await testSecretsRegistrySecret(secretId, { addOn: a.id }); setRes((p) => ({ ...p, [a.id]: r })); }
    catch (e) { setErr(e?.message || "Couldn't run the check"); }
    setBusy('');
  };
  return (
    <div className="mt-3 space-y-2" data-testid="connection-addons">
      {addOns.map((a) => {
        const r = res[a.id];
        const cases = r?.details?.cases || [];
        const bad = cases.filter((c) => !c.ok);
        return (
          <div key={a.id} className="rounded-md border border-gray-200 px-3 py-2 text-xs" data-testid={`addon-${a.id}`}>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-indigo-600" aria-hidden />
              <span className="font-medium text-gray-900">{a.label}</span>
              <span className="ml-auto flex gap-2">
                {a.template && !a.inTemplate ? (
                  <button type="button" className={`${btn} border border-gray-200 text-gray-700 hover:bg-gray-50`}
                    disabled={!!busy} onClick={() => download(a)} data-testid={`addon-template-${a.id}`}>
                    {busy === `t:${a.id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                    Setup template</button>
                ) : null}
                {a.check ? (
                  <button type="button" className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50`}
                    disabled={!!busy} onClick={() => check(a)} data-testid={`addon-check-${a.id}`}>
                    {busy === `c:${a.id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : null}Check</button>
                ) : null}
              </span>
            </div>
            {a.intro ? <p className="mt-1 text-gray-600">{a.intro}</p> : null}
            {r ? (
              <div className={`mt-2 flex items-start gap-1.5 rounded px-2 py-1.5 ${r.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-800'}`}
                data-testid={`addon-result-${a.id}`}>
                {r.ok ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" /> : <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
                <div className="min-w-0">
                  <div>{r.message}</div>
                  {bad.length ? (
                    <ul className="mt-1 list-disc pl-4" data-testid={`addon-failures-${a.id}`}>
                      {bad.map((c, i) => <li key={`${c.who}-${i}`}>{c.who}: {c.label} — expected {c.expect}, got {c.got}</li>)}
                    </ul>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        );
      })}
      {err ? <p className="text-[11px] text-rose-600" data-testid="addon-error">{err}</p> : null}
    </div>
  );
}
